export const ALL = 'all';
export const THIS_WEEK = 'this-week';

/**
 * VenueFinder filter key maps
 *
 * URL keys are lowercase kebab-case (SEO convention).
 * API/facet keys are PascalCase or display-format (backend convention).
 *
 * Simple keys (Facilities, Brand) where facet name matches API key
 * are mapped lowercase ↔ PascalCase directly.
 *
 * Compound keys (Agreement Type → AgreementType → agreement-type)
 * need an extra mapping step because the facet name has a space.
 */
export const VENUE_FINDER_URL_TO_API_KEY: Record<string, string> = {
  q: 'q',
  page: 'page',
  brand: 'Brand',
  facilities: 'Facilities',
  'agreement-type': 'AgreementType',
  'trading-style': 'TradingStyle',
  'location-type': 'LocationType',
};

export const VENUE_FINDER_API_TO_URL_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(VENUE_FINDER_URL_TO_API_KEY).map(([url, api]) => [api, url])
);

/**
 * Maps a facet display name to its URL key
 * Handles compound names like "Agreement Type" via API key intermediate step.
 * @param facetName - e.g. "Facilities", "Agreement Type"
 * @returns URL key - e.g. "facilities", "agreement-type"
 */
export const facetNameToUrlKey = (facetName: string): string => {
  const direct = VENUE_FINDER_API_TO_URL_KEY[facetName];
  if (direct) return direct;

  const asApi = facetName.replaceAll(' ', '');
  return VENUE_FINDER_API_TO_URL_KEY[asApi] || facetName.toLowerCase();
};

/**
 * Maps a URL key to a facet display name
 * @param urlKey - e.g. "facilities", "agreement-type"
 * @returns Facet name - e.g. "Facilities", "Agreement Type"
 */
export const urlKeyToFacetName = (urlKey: string): string => {
  const apiKey = VENUE_FINDER_URL_TO_API_KEY[urlKey];
  if (!apiKey || apiKey === 'q' || apiKey === 'page') return urlKey;

  const FACET_DISPLAY_NAMES: Record<string, string> = {
    Brand: 'Brand',
    Facilities: 'Facilities',
    AgreementType: 'Agreement Type',
    TradingStyle: 'Trading Style',
    LocationType: 'Location Type',
  };

  return FACET_DISPLAY_NAMES[apiKey] || apiKey;
};

/**
 * Converts a display value to URL-safe slug
 * @param value - Display value (e.g., "Live Music", "This Week")
 * @returns URL-safe slug (e.g., "live-music", "this-week")
 */
export const slugifyFilterValue = (value?: string | null): string => {
  return (
    value
      ?.toLowerCase()
      .trim()
      .replaceAll('&', ' and ')
      .replaceAll(/[^a-z0-9\s-]/g, '')
      .trim()
      .replaceAll(/\s+/g, '-') || ''
  );
};

/**
 * Converts month slug back to internal state value
 * @param slug - URL slug (e.g., "january", "this-week", "all")
 * @param availableMonths - Array of available month names
 * @returns Internal state value (e.g., "January", "this-week", "all")
 */
export const deslugifyMonth = (
  slug: string | null | undefined,
  availableMonths: string[] = []
): string | null => {
  if (!slug) return null;

  const normalizedSlug = slug.toLowerCase();

  if (normalizedSlug === ALL) return ALL;
  if (normalizedSlug === THIS_WEEK) return THIS_WEEK;

  const matchedMonth = availableMonths.find((month) => month.toLowerCase() === normalizedSlug);

  return matchedMonth || null;
};

/**
 * Converts a URL slug back to a display value by matching against available values.
 * Handles the ALL constant. Works for any filter type (categories, facilities, brands, etc.).
 * @param slug - URL slug (e.g., "beer-garden", "hungry-horse", "all")
 * @param availableValues - Array of display values from API (e.g., ["Beer Garden", "Hungry Horse"])
 * @returns Matched display value, ALL constant, or null if no match
 */
export const deslugifyValue = (
  slug: string | null | undefined,
  availableValues: string[] = []
): string | null => {
  if (!slug) return null;

  const normalizedSlug = slug.toLowerCase();

  if (normalizedSlug === ALL) return ALL;

  const matched = availableValues.find((value) => slugifyFilterValue(value) === normalizedSlug);

  return matched || null;
};
